import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Box, FormHelperText } from "@mui/material";
import { MDTypography } from "../../../../Component/Common/Components/MDTypography";
import { Quill } from "../../../../Component/Common/Components/Quill/Quill";
import { WorkforceProfileGoalsAndTargetsService } from "../../../../Component/Api/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileGoalsAndTargetsService";
import { WorkforceProfileGoalsAndTargets } from "../../../../Component/Model/Dto/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileGoalsAndTargets";
import { EEOccupationalLevel } from "../../../../Component/Model/Enum/EmploymentEquity";
import { workforceCommentSchema } from "./WorkforceCommentValidation";
import { toast } from "react-toastify";
import { EEScorecardAtom } from "../../../../State/EEScorecard.Atom";
import { useRecoilValue } from "recoil";
import { ConsolidationAtom } from "../../../../State/Consolidation.Atom";

interface IProps {
    comment?: WorkforceProfileGoalsAndTargets.CommentsDtoOut[];
    occupationalLevel: EEOccupationalLevel;
    scorecardId: string;
    isDisabled: boolean;
}

type CommentFormData = {
    comment: string;
}

export const WorkforceComments: FC<IProps> = (props) => {
    const [initialComment, setInitialComment] = useState<WorkforceProfileGoalsAndTargets.CommentsDtoOut | null>(null);
    const initialCommentRef = useRef<WorkforceProfileGoalsAndTargets.CommentsDtoOut | null>(null);
    const scorecard = useRecoilValue(EEScorecardAtom);
    const isConsolidatedScorecard = useRecoilValue(ConsolidationAtom);
    const maxConsolidatedCommentLength = 3000 * (scorecard.ConsolidationSourceScorecardsCount ?? 1);
    const maxCommentLength = isConsolidatedScorecard ? maxConsolidatedCommentLength : 3000;

    const { control, reset, getValues, setValue, formState: { errors } } = useForm<CommentFormData>({
        mode: 'all',
        resolver: yupResolver(workforceCommentSchema(maxCommentLength))
    });

    useEffect(() => {
        const matchingComment = props.comment?.find(comment => comment.occupationalLevel === props.occupationalLevel);
        if (matchingComment) {
            setInitialComment(matchingComment);
            initialCommentRef.current = matchingComment;
            reset({ comment: matchingComment.comment });
        } else {
            setInitialComment(null);
            initialCommentRef.current = null;
            reset({ comment: '' });
        }
    }, [props.comment, props.occupationalLevel, reset]);

    useEffect(() => {
        return () => {
            if (!errors.comment && localStorage.getItem('coreToken') !== null) {
                saveCommentForm();
            }
        };
    }, [getValues]);

    const saveCommentForm = async () => {
        const formData = getValues();
        if (formData.comment.length > 0) {
            workforceCommentSchema(maxCommentLength).validate(formData, { abortEarly: false })
                .then(async () => {
                    const requestData = {
                        commentId: initialCommentRef.current?.commentId,
                        comment: formData.comment,
                        isDisabled: props.isDisabled,
                        occupationalLevel: props.occupationalLevel,
                        scorecardId: props.scorecardId,
                    };

                    await WorkforceProfileGoalsAndTargetsService.SaveComment(requestData);
                })
                .catch(function (error: any) {
                    toast.error(error.message);
                });
        }
    };

    return (
        <Box mt={2}>
            <MDTypography variant='button' fontWeight='regular' style={{ color: !!errors.comment ? 'error' : '#666666', fontSize: "0.85em" }}>
                Comments for EEA12 Report (Max {maxCommentLength} characters)
            </MDTypography>
            <Controller
                control={control}
                name="comment"
                defaultValue={initialComment?.comment || ''}
                render={({ field: { onChange, value } }) => (
                    <Quill
                        initialContent={value || ""}
                        handleChange={(newValue: string) => {
                            onChange(newValue);
                            setValue('comment', newValue);
                        }}
                        maxLength={maxCommentLength}
                    />
                )}
            />
            {!!errors.comment && <FormHelperText error>{errors.comment.message}</FormHelperText>}
        </Box>
    );
};
